import { Transaction } from '@/types';
import { generateBillPDF } from '@/lib/reports/bill-pdf';

export interface BillEmailData {
  to: string;
  subject: string;
  message: string;
  transaction: Transaction;
  client?: {
    name: string;
    email?: string;
    phone?: string;
    documentNumber?: string;
    address?: any;
  };
  billNumber: string;
  issueDate: Date;
  companyName: string;
  companyLogo?: string;
  bankDetails?: {
    bank?: string;
    agency?: string;
    account?: string;
    pixKey?: string;
  };
  includePDF: boolean;
}

/**
 * Send bill email
 */
export async function sendBillEmail(data: BillEmailData): Promise<void> {
  try {
    const { to, subject, message, includePDF, ...billData } = data;

    // Import email service dynamically
    const { sendEmail } = await import('./emailNotificationService');

    // Generate PDF if needed
    let pdfBlob: Blob | null = null;
    if (includePDF) {
      // Create a temporary PDF generation function that returns a blob
      pdfBlob = await generateBillPDFBlob(billData);
    }

    // Prepare email content with HTML template
    const htmlContent = generateBillEmailHTML({
      transaction: data.transaction,
      client: data.client,
      billNumber: data.billNumber,
      companyName: data.companyName,
      message: data.message,
      bankDetails: data.bankDetails,
    });

    // Create attachments array
    const attachments: Array<{ filename: string; blob: Blob }> = [];
    if (pdfBlob) {
      const timestamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      attachments.push({
        filename: `cobranca_${data.billNumber}_${timestamp}.pdf`,
        blob: pdfBlob,
      });
    }

    // Send email using the existing email service
    await sendEmail({
      to: [to],
      subject,
      html: htmlContent,
      attachments,
      type: 'bill',
    });
  } catch (error) {
    console.error('Error sending bill email:', error);
    throw new Error('Falha ao enviar a cobrança por e-mail. Por favor, tente novamente.');
  }
}

/**
 * Generate bill PDF as blob for email attachment
 */
async function generateBillPDFBlob(data: {
  transaction: Transaction;
  client?: {
    name: string;
    email?: string;
    phone?: string;
    documentNumber?: string;
    address?: any;
  };
  bankDetails?: {
    bank?: string;
    agency?: string;
    account?: string;
    pixKey?: string;
  };
  billNumber: string;
  issueDate: Date;
  companyName: string;
  companyLogo?: string;
  companyDocument?: string;
  companyAddress?: any;
}): Promise<Blob> {
  // Import jsPDF
  const jsPDF = (await import('jspdf')).default;

  return new Promise((resolve, reject) => {
    try {
      const { transaction, client, bankDetails, billNumber, issueDate, companyName } = data;

      // Create new PDF document
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });

      // Get page dimensions
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 20;
      let yPosition = margin;

      // Simplified bill generation for email
      doc.setFontSize(20);
      doc.text(companyName, margin, yPosition);
      yPosition += 15;

      doc.setFontSize(24);
      doc.text('COBRANÇA', margin, yPosition);
      yPosition += 10;

      doc.setFontSize(12);
      doc.text(`Nº ${billNumber}`, margin, yPosition);
      yPosition += 7;
      doc.text(`Emitido em: ${issueDate.toLocaleDateString('pt-BR')}`, margin, yPosition);
      yPosition += 15;

      // Transaction details
      doc.setFontSize(12);
      doc.text('Descrição: ' + transaction.description, margin, yPosition);
      yPosition += 8;
      doc.text('Valor: ' + formatCurrency(transaction.amount), margin, yPosition);
      yPosition += 8;
      doc.text('Vencimento: ' + (transaction.due_date ? new Date(transaction.due_date).toLocaleDateString('pt-BR') : 'Não definido'), margin, yPosition);
      yPosition += 8;

      if (client) {
        doc.text('Cliente: ' + client.name, margin, yPosition);
        yPosition += 8;
      }

      if (bankDetails) {
        yPosition += 5;
        doc.text('Dados para Pagamento:', margin, yPosition);
        yPosition += 8;
        if (bankDetails.bank) {
          doc.text('Banco: ' + bankDetails.bank, margin + 5, yPosition);
          yPosition += 6;
        }
        if (bankDetails.pixKey) {
          doc.text('PIX: ' + bankDetails.pixKey, margin + 5, yPosition);
          yPosition += 6;
        }
      }

      // Generate blob
      const pdfBlob = doc.output('blob');
      resolve(pdfBlob);
    } catch (error) {
      reject(error);
    }
  });
}

/**
 * Generate HTML email template for bill
 */
function generateBillEmailHTML(data: {
  transaction: Transaction;
  client?: {
    name: string;
    email?: string;
    phone?: string;
    documentNumber?: string;
    address?: any;
  };
  billNumber: string;
  companyName: string;
  companyLogo?: string;
  message: string;
  bankDetails?: {
    bank?: string;
    agency?: string;
    account?: string;
    pixKey?: string;
  };
}): string {
  const { transaction, client, billNumber, companyName, companyLogo, message, bankDetails } = data;

  // Calculate days until due
  const getDaysUntilDue = () => {
    if (!transaction.due_date) return null;
    const due = new Date(transaction.due_date);
    const today = new Date();
    const diffTime = due.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const daysUntilDue = getDaysUntilDue();

  return `
<!DOCTYPE html>
<html lang="pt-BR">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Cobrança ${billNumber}</title>
    <style>
        body {
            font-family: Arial, sans-serif;
            line-height: 1.6;
            color: #333;
            max-width: 600px;
            margin: 0 auto;
            padding: 20px;
            background-color: #f4f4f4;
        }
        .container {
            background-color: white;
            padding: 30px;
            border-radius: 8px;
            box-shadow: 0 2px 10px rgba(0,0,0,0.1);
        }
        .header {
            text-align: center;
            border-bottom: 2px solid #e0e0e0;
            padding-bottom: 20px;
            margin-bottom: 20px;
        }
        .header h1 {
            color: #d97706; /* terracotta color */
            margin: 10px 0;
            font-size: 28px;
        }
        .company-logo {
            max-width: 120px;
            max-height: 60px;
            margin-bottom: 10px;
        }
        .header p {
            color: #7f8c8d;
            margin: 5px 0;
        }
        .bill-details {
            background-color: #fef3c7;
            padding: 20px;
            border-radius: 5px;
            margin: 20px 0;
            border-left: 4px solid #d97706;
        }
        .detail-row {
            display: flex;
            justify-content: space-between;
            margin-bottom: 10px;
            padding: 8px 0;
            border-bottom: 1px solid #e0e0e0;
        }
        .detail-row:last-child {
            border-bottom: none;
        }
        .detail-label {
            font-weight: bold;
            color: #555;
        }
        .detail-value {
            color: #333;
        }
        .amount {
            font-size: 24px;
            font-weight: bold;
            color: #d97706;
        }
        .client-info {
            background-color: #ecf0f1;
            padding: 15px;
            border-radius: 5px;
            margin: 20px 0;
        }
        .payment-info {
            background-color: #f8f9fa;
            padding: 15px;
            border-radius: 5px;
            margin: 20px 0;
        }
        .payment-info h4 {
            color: #d97706;
            margin-bottom: 10px;
        }
        .urgent {
            background-color: #fee2e2;
            border-left: 4px solid #dc2626;
            padding: 15px;
            border-radius: 5px;
            margin: 20px 0;
        }
        .warning {
            background-color: #fef3c7;
            border-left: 4px solid #f59e0b;
            padding: 15px;
            border-radius: 5px;
            margin: 20px 0;
        }
        .message {
            margin: 20px 0;
            padding: 15px;
            background-color: #f0f9ff;
            border-left: 4px solid #3b82f6;
            border-radius: 4px;
        }
        .footer {
            text-align: center;
            margin-top: 30px;
            padding-top: 20px;
            border-top: 1px solid #e0e0e0;
            color: #7f8c8d;
            font-size: 14px;
        }
        @media print {
            body { background-color: white; }
            .container { box-shadow: none; }
        }
    </style>
</head>
<body>
    <div class="container">
        <div class="header">
            ${companyLogo ? `<img src="${companyLogo}" alt="${companyName}" class="company-logo">` : ''}
            <h1>${companyName}</h1>
            <p><strong>COBRANÇA Nº ${billNumber}</strong></p>
            <p>Emitido em: ${new Date().toLocaleDateString('pt-BR')}</p>
        </div>

        <div class="bill-details">
            <div class="detail-row">
                <span class="detail-label">Descrição:</span>
                <span class="detail-value">${transaction.description}</span>
            </div>
            <div class="detail-row">
                <span class="detail-label">Valor da Cobrança:</span>
                <span class="detail-value amount">${formatCurrency(transaction.amount)}</span>
            </div>
            <div class="detail-row">
                <span class="detail-label">Data de Vencimento:</span>
                <span class="detail-value">${
                  transaction.due_date
                    ? new Date(transaction.due_date).toLocaleDateString('pt-BR')
                    : 'Não definido'
                }</span>
            </div>
            ${transaction.category ? `
            <div class="detail-row">
                <span class="detail-label">Categoria:</span>
                <span class="detail-value">${transaction.category}</span>
            </div>
            ` : ''}
        </div>

        ${daysUntilDue !== null ? (
          daysUntilDue < 0 ? `
          <div class="urgent">
              <strong>⚠️ ATENÇÃO: Cobrança em Atraso!</strong><br>
              Esta cobrança está ${Math.abs(daysUntilDue)} dias atrasada. Por favor, regularize o pagamento o mais breve possível para evitar multas e juros adicionais.
          </div>
          ` : daysUntilDue <= 7 ? `
          <div class="warning">
              <strong>⏰ AVISO: Vencimento Próximo!</strong><br>
              Esta cobrança vencerá em ${daysUntilDue === 0 ? 'hoje' : `${daysUntilDue} dias`}. Efetue o pagamento para evitar multas.
          </div>
          ` : ''
        ) : ''}

        ${client ? `
        <div class="client-info">
            <h3>Dados do Cliente</h3>
            <p><strong>Nome:</strong> ${client.name}</p>
            ${client.documentNumber ? `<p><strong>CPF/CNPJ:</strong> ${client.documentNumber}</p>` : ''}
            ${client.email ? `<p><strong>E-mail:</strong> ${client.email}</p>` : ''}
            ${client.phone ? `<p><strong>Telefone:</strong> ${client.phone}</p>` : ''}
        </div>
        ` : ''}

        ${bankDetails ? `
        <div class="payment-info">
            <h4>Dados para Pagamento</h4>
            ${bankDetails.bank ? `<p><strong>Banco:</strong> ${bankDetails.bank}</p>` : ''}
            ${bankDetails.agency ? `<p><strong>Agência:</strong> ${bankDetails.agency}</p>` : ''}
            ${bankDetails.account ? `<p><strong>Conta:</strong> ${bankDetails.account}</p>` : ''}
            ${bankDetails.pixKey ? `<p><strong>Chave PIX:</strong> ${bankDetails.pixKey}</p>` : ''}
        </div>
        ` : ''}

        <div class="message">
            ${message.replace(/\n/g, '<br>')}
        </div>

        <div class="footer">
            <p>Esta é uma cobrança oficial gerada pelo sistema.</p>
            <p>${companyName} - ${new Date().toLocaleDateString('pt-BR')}</p>
            <p><small>Em caso de dúvidas, entre em contato conosco.</small></p>
        </div>
    </div>
</body>
</html>
  `;
}

/**
 * Format currency value
 */
function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
}

/**
 * Generate unique bill number
 */
export function generateBillNumber(transactionId: string): string {
  const year = new Date().getFullYear();
  const idSuffix = String(transactionId).slice(-6).padStart(6, '0');
  return `F-${year}${idSuffix}`;
}